/**
 * space_ops_client.ts — OPERATIONS WITH OBJECTIVES, the client's half (space campaign slice H, 2026-10-01).
 *
 * The rows (public, subscribed lazily once the sector is live), the reducer calls the board and the driver make, and the
 * per-frame DRIVER: while the pilot holds within reach of an objective it sends the metered beats the server judges
 * (≤ 1 Hz, one in flight) — the scout's verification at its route marker, the escort pilot's dwell in the refuge. The
 * server decides everything (range, line of sight, interference, speed, the war's clock); the client only keeps
 * asking while there is something to ask about. Nothing here ever claims an objective: evidence is the other systems'
 * receipts (THE EVIDENCE LAW, server/src/lib/space_operations_ops.ts).
 */
import type { DbConnection } from '../module_bindings/index.js';
import { Identity } from 'spacetimedb';
import {
  opBeacon, extractionPose, SCOUT_RANGE_M, REFUGE_R_M, OP_TEMPLATES, type OpAlloc, type OpTemplate,
} from '@shared/space_operation_rules.js';
import { MASTER_TITLE, isCouncilTitle } from './guild_rules.js';
import { extractionSeats } from './space_ops_lines.js';

export const hexOf = (id: Identity): string => id.toHexString().replace(/^0x/, '').toLowerCase();
const subscribed = new WeakSet<object>();
/** The operation rows + my own guild membership + the evidence sources a pilot may submit (their RLS keeps them mine). */
export function ensureOpsSubs(conn: DbConnection): void {
  if (subscribed.has(conn)) return;
  subscribed.add(conn);
  (globalThis as { __spaceOps?: () => Record<string, unknown> }).__spaceOps = () => opsTelemetry(conn);
  try {
    conn.subscriptionBuilder().subscribe([
      'SELECT * FROM space_operation', 'SELECT * FROM op_objective', 'SELECT * FROM op_enrollment', 'SELECT * FROM op_receipt',
      'SELECT * FROM op_meter', 'SELECT * FROM escort_extraction', 'SELECT * FROM campaign_ops_pin',
      'SELECT * FROM guild_member WHERE identity = :sender', 'SELECT * FROM guild', 'SELECT * FROM port_receipt', 'SELECT * FROM subsystem_receipt',
    ]);
  } catch { /* pre-publish */ }
}

type OpRow = ReturnType<DbConnection['db']['spaceOperation']['opId']['find']> & object;
type ObjRow = ReturnType<DbConnection['db']['opObjective']['objKey']['find']> & object;
type ExtRow = ReturnType<DbConnection['db']['escortExtraction']['extractKey']['find']> & object;
export type { OpRow, ObjRow, ExtRow };

export function opsRows(conn: DbConnection): OpRow[] { try { return [...conn.db.spaceOperation.iter()]; } catch { return []; } }
export function objOf(conn: DbConnection, opId: bigint, t: OpTemplate): ObjRow | null { try { return conn.db.opObjective.objKey.find(`${opId}:${t}`) ?? null; } catch { return null; } }
export function allocOf(op: OpRow): OpAlloc {
  try { const a = JSON.parse(op.allocJson) as OpAlloc; return { scout: a.scout | 0, haul: a.haul | 0, repair: a.repair | 0, combat: a.combat | 0 }; } catch { return { scout: 0, haul: 0, repair: 0, combat: 0 }; }
}
export function enrolled(conn: DbConnection, opId: bigint, t: OpTemplate): boolean {
  const me = conn.identity;
  if (!me) return false;
  try { return !!conn.db.opEnrollment.enrollKey.find(`${opId}:${t}:${hexOf(me)}`); } catch { return false; }
}
export function receiptsOf(conn: DbConnection, opId: bigint): Array<{ template: string; slot: string; amountSov: bigint; beneficiary: Identity; order: number; evidenceId: string }> {
  try { return [...conn.db.opReceipt.iter()].filter((r) => r.opId === opId).sort((a, b) => a.order - b.order); } catch { return []; }
}
export function extractionsOf(conn: DbConnection): ExtRow[] { try { return [...conn.db.escortExtraction.iter()]; } catch { return []; } }
/** My guild and whether I sit on its court (the Guild Master or a councillor) — the funding form shows only then. */
export function myCourt(conn: DbConnection): { guildId: bigint; name: string } | null {
  const me = conn.identity;
  if (!me) return null;
  try {
    for (const m of conn.db.guildMember.iter()) {
      if (!m.identity.isEqual(me)) continue;
      if (m.title !== MASTER_TITLE && !isCouncilTitle(m.title)) return null;
      const g = conn.db.guild.guildId.find(m.guildId);
      return { guildId: m.guildId, name: g ? `${g.name} <${g.tag}>` : `guild ${m.guildId}` };
    }
  } catch { /* pre-publish */ }
  return null;
}

// ── THE CALLS ──────────────────────────────────────────────────────────────────────────────────────────────────────
type Toast = (t: string) => void;
const say = (toast: Toast | undefined, ok: string) => (p: Promise<unknown>): Promise<boolean> => p.then(() => { toast?.(ok); return true; }, (e: unknown) => { toast?.(`✗ ${String((e as Error)?.message ?? e)}`); return false; });
export function propose(conn: DbConnection, a: { guildId: bigint; side: number; cyc: bigint } & OpAlloc, toast?: Toast): Promise<boolean> {
  return say(toast, '⛭ Operation proposed — the court\'s motion funds it')(conn.reducers.proposeSpaceOperation({ guildId: a.guildId, side: a.side, cyc: a.cyc, scout: a.scout, haul: a.haul, repair: a.repair, combat: a.combat }));
}
export function cancelOp(conn: DbConnection, opId: bigint, toast?: Toast): Promise<boolean> {
  return say(toast, `⛭ Cancellation laid before the court (operation #${opId})`)(conn.reducers.cancelSpaceOperation({ opId }));
}
export function enroll(conn: DbConnection, opId: bigint, t: OpTemplate, toast?: Toast): Promise<boolean> {
  return say(toast, `⛭ Enrolled: operation #${opId}`)(conn.reducers.enrollSpaceOperation({ opId, template: t, crew: [] }));
}
export function startExtraction(conn: DbConnection, opId: bigint, cls: string, slot: number, toast?: Toast): Promise<boolean> {
  return say(toast, `⇲ Extraction ordered — the ${cls} flies for its refuge; screen it, then hold the refuge 10 s`)(conn.reducers.opStartExtraction({ opId, cls, slot }));
}
/** Submit every receipt of mine the operation could count (a landed run, an eligible patch). The sweep collects them
 *  too (every 10 s) — this only saves the wait. */
export async function submitMine(conn: DbConnection, opId: bigint, toast?: Toast): Promise<number> {
  const me = conn.identity;
  if (!me) return 0;
  const ids: string[] = [];
  try {
    for (const r of conn.db.portReceipt.iter()) if (r.direction === 'up' && (r.captain.isEqual(me) || r.deliveredBy.isEqual(me))) ids.push(`port:${r.haulId}`);
    for (const r of conn.db.subsystemReceipt.iter()) if (r.actor.isEqual(me) && r.outcome === 'patched' && r.eligibleRestored > 0) ids.push(`patch:${r.receiptKey}`);
  } catch { /* pre-publish */ }
  let n = 0;
  for (const id of ids.slice(-12)) {
    try { await conn.reducers.opSubmitEvidence({ opId, evidenceId: id }); n++; } catch { /* not this operation's (the server says why) */ }
  }
  toast?.(n ? `⛭ ${n} receipt${n === 1 ? '' : 's'} checked against operation #${opId}` : 'No receipt of yours fits this operation yet');
  return n;
}

// ── THE DRIVER (per frame; ≤ 1 beat a second, one in flight) ───────────────────────────────────────────────────────────
export interface OpsHost {
  conn: DbConnection | null; systemId: string;
  /** The ship's ABSOLUTE sector position. */
  pos: { x: number; y: number; z: number };
}
const drive = { lastAt: 0, inflight: false, lastError: '', lastKind: '' };
export function opsDriverState(): { lastAt: number; inflight: boolean; lastError: string; lastKind: string } { return { ...drive }; }
const d3 = (a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }): number => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
export function tickSpaceOps(h: OpsHost, nowMs: number): void {
  const conn = h.conn;
  if (!conn?.identity || h.systemId !== 'sys0') return;
  ensureOpsSubs(conn);
  if (drive.inflight || nowMs - drive.lastAt < 1050) return;
  const me = conn.identity;
  try { if (conn.db.spaceLocation.identity.find(me)?.mode !== 'flight') return; } catch { return; }
  let call: (() => Promise<void>) | null = null, kind = '';
  for (const op of opsRows(conn)) {
    if (op.state !== 'active') continue;
    // The escort pilot's dwell: an extraction of mine, the escort arrived, me inside twice the refuge.
    for (const e of extractionsOf(conn)) {
      if (e.opId !== op.opId || e.state !== 'extracting' || !e.pilot.isEqual(me)) continue;
      const pose = extractionPose(e, nowMs / 1000);
      if (pose.arrived && d3(h.pos, { x: e.rx, y: e.ry, z: e.rz }) <= REFUGE_R_M * 2) { call = () => conn.reducers.opExtractionBeat({ opId: op.opId }); kind = 'dwell'; }
    }
    if (call) break;
    // The scout: my objective, still open, me within reach of its marker.
    const o = objOf(conn, op.opId, 'scout');
    if (o && o.state === 'open' && o.beneficiary.isEqual(me)) {
      const b = opBeacon(Number(op.cyc), op.opId);
      if (d3(h.pos, b.pos) <= SCOUT_RANGE_M * 3) { call = () => conn.reducers.opScoutBeat({ opId: op.opId }); kind = 'scout'; break; }
    }
  }
  if (!call) return;
  drive.inflight = true; drive.lastAt = nowMs; drive.lastKind = kind;
  call().then(() => { drive.lastError = ''; }, (e: unknown) => { drive.lastError = String((e as Error)?.message ?? e); }).finally(() => { drive.inflight = false; });
}
export const TEMPLATES = OP_TEMPLATES;

// ── THE SEAT (SpaceModule.updateWarEscorts, every frame): an extracting escort flies the server's declared course; a
// withdrawn one holds its refuge — out of the fight, its FSM parked (THE DEN POSITION LAW's seat, reused) ─────────────
export interface SeatablePirate { key: string; deadUntil: number; seat?: { x: number; y: number; z: number; yaw: number; at: number; stamp: bigint } }
const EXTRACT_STAMP = -7n; // marks a seat this module set (a den seat carries a row's real stamp)
let lastSeats = 0;
export function seatExtractions(pirates: SeatablePirate[], conn: DbConnection | null, campaignId: bigint | null, nowP: number): number {
  if (!conn || campaignId === null) { lastSeats = 0; return 0; }
  const seats = extractionSeats(extractionsOf(conn), campaignId, Date.now() / 1000);
  let n = 0;
  for (const p of pirates) {
    if (p.deadUntil > 0) continue;
    const s = seats.get(p.key);
    if (s) { p.seat = { x: s.pos.x, y: s.pos.y, z: s.pos.z, yaw: s.yaw, at: nowP, stamp: EXTRACT_STAMP }; n++; }
    else if (p.seat?.stamp === EXTRACT_STAMP) p.seat = undefined; // a failed extraction hands the hull back to its FSM
  }
  lastSeats = n;
  return n;
}
/** Probe lever: `__spaceOps()` — the rows this client holds, the driver, the seats. */
export function opsTelemetry(conn: DbConnection | null): Record<string, unknown> {
  if (!conn) return { conn: false };
  const n = (f: () => Iterable<unknown>): number => { try { return [...f()].length; } catch { return -1; } };
  return {
    operations: n(() => conn.db.spaceOperation.iter()), objectives: n(() => conn.db.opObjective.iter()), receipts: n(() => conn.db.opReceipt.iter()),
    extractions: n(() => conn.db.escortExtraction.iter()), driver: opsDriverState(), seats: lastSeats,
  };
}
